import { useState, useEffect } from 'react';
import { C, FONT, Card, SectionHeader, fmtDate } from './adminShared';
import { getAdminTestimonials } from '../../utils/api';
import { TESTIMONIAL_QUESTIONS, TESTIMONIAL_FEEDBACK_QUESTION } from '../../utils/constants';

// Star colours match the portal's StarRating / CompletionFlow (gold #F5A623 fill,
// '#ddd' empty). C.cta is the same gold token used across the admin dashboard.
const STAR_GOLD = C.cta;
const STAR_EMPTY = '#ddd';

// ── Star primitives (kept tiny, local to this file) ───────────────────────────
// One star, fill = 'full' | 'half' | 'empty'. 'half' overlays a 50%-clipped gold
// star on a grey base.
const Star = ({ fill, size = 22 }) => {
  const base = { display: 'inline-block', width: size, lineHeight: `${size}px`, fontSize: size, textAlign: 'center' };
  if (fill === 'half') {
    return (
      <span style={{ position: 'relative', ...base }}>
        <span style={{ color: STAR_EMPTY }}>★</span>
        <span style={{ position: 'absolute', left: 0, top: 0, width: '50%', overflow: 'hidden', color: STAR_GOLD }}>★</span>
      </span>
    );
  }
  return <span style={{ ...base, color: fill === 'full' ? STAR_GOLD : STAR_EMPTY }}>★</span>;
};

// Average → 5 stars with at most one half. floor(avg) full; remainder >= 0.75 ⇒
// promote to a full star; remainder in [0.25, 0.75) ⇒ one half; else empty.
const AvgStars = ({ avg, size = 22 }) => {
  let full = Math.floor(avg);
  const rem = avg - full;
  let half = false;
  if (rem >= 0.75) full += 1;
  else if (rem >= 0.25) half = true;
  return (
    <span style={{ display: 'inline-flex', gap: 3 }}>
      {[0, 1, 2, 3, 4].map(i => (
        <Star key={i} size={size} fill={i < full ? 'full' : (i === full && half ? 'half' : 'empty')} />
      ))}
    </span>
  );
};

// Whole-star row for an individual integer rating (1–5).
const WholeStars = ({ n, size = 18 }) => (
  <span style={{ display: 'inline-flex', gap: 2 }}>
    {[0, 1, 2, 3, 4].map(i => <Star key={i} size={size} fill={i < n ? 'full' : 'empty'} />)}
  </span>
);

export const AdminTestimonials = () => {
  const [data, setData]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [openId, setOpenId]   = useState(null); // one-open-at-a-time accordion

  useEffect(() => {
    let active = true;
    getAdminTestimonials()
      .then(res => { if (active) setData(Array.isArray(res.data) ? res.data : []); })
      .catch(err => { if (active) setError(err.response?.data?.error || err.message); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  // ── A) "All" per-question averages across everyone who answered ──────────────
  const allAverages = TESTIMONIAL_QUESTIONS.map((q, idx) => {
    const vals = data
      .map(t => t[`star_${idx + 1}`])
      .filter(v => typeof v === 'number' && v >= 1);
    const n = vals.length;
    const avg = n ? vals.reduce((a, b) => a + b, 0) / n : 0;
    return { q, avg, n };
  });

  return (
    <div style={{ fontFamily: FONT }}>
      <SectionHeader
        title="Testimonials"
        sub={loading
          ? 'Loading…'
          : error
            ? 'Failed to load'
            : `${data.length} client testimonial${data.length === 1 ? '' : 's'} · averages computed live in the browser`}
      />

      {error && (
        <Card style={{ marginBottom: 20, borderColor: C.red }}>
          <span style={{ color: C.red, fontSize: 13 }}>✗ Failed to load testimonials: {error}</span>
        </Card>
      )}

      {!loading && !error && data.length === 0 && (
        <Card>
          <span style={{ fontSize: 13, color: C.accent }}>
            No testimonials yet — they'll appear here once clients complete the program.
          </span>
        </Card>
      )}

      {!loading && !error && data.length > 0 && (
        <>
          {/* ── A) ALL summary block (screenshot target) ── */}
          <Card title="All Testimonials — Question Averages" style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              {allAverages.map((row, idx) => (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10,
                    paddingBottom: idx < allAverages.length - 1 ? 18 : 0,
                    borderBottom: idx < allAverages.length - 1 ? `1px solid ${C.border}` : 'none',
                  }}
                >
                  <div style={{ fontSize: 14, color: C.text, fontWeight: 600, lineHeight: 1.5 }}>
                    {row.q}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                    <AvgStars avg={row.avg} />
                    <span style={{ fontSize: 16, fontWeight: 800, color: C.heading }}>
                      {row.avg.toFixed(1)} / 5
                    </span>
                    <span style={{ fontSize: 12, color: C.accent }}>(n={row.n})</span>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* ── B) Per-person accordion (MyJournal pattern, one open at a time) ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {data.map(t => {
              const isOpen = openId === t.id;
              const u = t.ry_users || {};
              const name = `${u.first_name || ''} ${u.last_name || ''}`.trim() || 'Client';
              const email = u.email || '';
              const rated = [t.star_1, t.star_2, t.star_3, t.star_4, t.star_5]
                .filter(v => typeof v === 'number' && v >= 1);
              const personAvg = rated.length ? rated.reduce((a, b) => a + b, 0) / rated.length : 0;
              const hasFeedback = typeof t.feedback_text === 'string' && t.feedback_text.trim().length > 0;

              return (
                <div
                  key={t.id}
                  style={{
                    background: C.card,
                    borderRadius: 12,
                    border: `1.5px solid ${isOpen ? C.cta : C.border}`,
                    overflow: 'hidden',
                    transition: 'border-color 0.2s',
                    boxShadow: isOpen ? '0 4px 20px rgba(245,166,35,0.12)' : '0 1px 4px rgba(0,0,0,0.06)',
                  }}
                >
                  {/* Closed/header row — clickable */}
                  <button
                    onClick={() => setOpenId(isOpen ? null : t.id)}
                    style={{
                      width: '100%', background: 'none', border: 'none',
                      padding: '16px 20px', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 14,
                      textAlign: 'left', fontFamily: FONT,
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 800, color: C.heading }}>{name}</div>
                      <div style={{ fontSize: 12, color: C.accent, marginTop: 2 }}>
                        {email}{email && t.submitted_at ? ' · ' : ''}{t.submitted_at ? fmtDate(t.submitted_at) : ''}
                      </div>
                    </div>
                    <span style={{ fontSize: 12, fontWeight: 700, color: C.accent, whiteSpace: 'nowrap' }}>
                      {personAvg.toFixed(1)}/5
                    </span>
                    <span style={{
                      fontSize: 16, color: C.cta, flexShrink: 0,
                      transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s',
                    }}>
                      ▾
                    </span>
                  </button>

                  {/* Expanded body — 5 questions w/ whole stars, then feedback */}
                  {isOpen && (
                    <div style={{
                      borderTop: `1px solid ${C.border}`,
                      padding: '18px 20px 22px',
                      display: 'flex', flexDirection: 'column', gap: 16,
                    }}>
                      {TESTIMONIAL_QUESTIONS.map((q, idx) => {
                        const n = t[`star_${idx + 1}`];
                        const hasN = typeof n === 'number' && n >= 1;
                        return (
                          <div key={idx} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <div style={{ fontSize: 13, color: C.text, lineHeight: 1.5 }}>{q}</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <WholeStars n={hasN ? n : 0} />
                              <span style={{ fontSize: 12, fontWeight: 700, color: C.accent }}>
                                {hasN ? `${n}/5` : '—'}
                              </span>
                            </div>
                          </div>
                        );
                      })}

                      {hasFeedback && (
                        <div style={{ marginTop: 4 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: C.heading, marginBottom: 8, lineHeight: 1.5 }}>
                            {TESTIMONIAL_FEEDBACK_QUESTION}
                          </div>
                          <div style={{
                            background: C.bg,
                            border: `1px solid ${C.border}`,
                            borderRadius: 10,
                            padding: '14px 16px',
                            fontSize: 14, color: C.text, lineHeight: 1.65, fontStyle: 'italic',
                          }}>
                            "{t.feedback_text}"
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};
